import * as React from "react"
import { Col, Row } from "react-grid-system"
import styled from "styled-components"
import Card from "../../atoms/Card"
import CardBar from "../../atoms/CardBar"
import H4 from "../../atoms/typography/H4"
import Subtitle from "../../atoms/typography/Subtitle"
import HoverLinkText from "../../atoms/buttons/HoverLinkText"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { AddIcon } from "../../atoms/icons/AddIcon"
import AddressForm from "../../molecules/AddressForm"
import TextInput from "../../molecules/TextInput"
import { useCreateNewOrganization } from "../../../hooks/useCreateNewOrganization"
import { useUserJoinOrgRequest } from "../../../hooks/useUserJoinOrgRequest"
import { MediaQuery } from "../../../utils/design-helpers"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { useAcceptOrgJoinRequest } from "../../../hooks/useAcceptOrgJoinRequest"
import { useRemoveOrgJoinRequest } from "../../../hooks/useRemoveOrganizationJoinRequest"
import Body from "../../atoms/typography/Body"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"

const CardContent = styled.div`
  display: flex;
  justify-content: center;
  padding: ${(props) => props.theme.spacings.large};
  flex-direction: column;
`

const Image = styled.img`
  width: 600px;
  height: 600px;
  border-radius: 50%;
  object-fit: cover;

  ${MediaQuery.XS} {
    width: 95vw;
    height: 95vw;
  }
`

const OrganizationContent = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};
`

const LandingPage: React.FunctionComponent = () => {
  const [addOrgOpen, setAddOrgOpen] = React.useState(false)
  const { userJoinOrgRequest } = useUserJoinOrgRequest()

  const [error, setError] = React.useState(false)

  const [newOrgName, setNewOrgName] = React.useState("")
  const [billingAddress, setBillingAddress] = React.useState<
    | undefined
    | {
        address1?: string
        address2?: string
        country?: string
        city?: string
        zipCode?: string
        vat?: string
      }
  >(undefined)

  const handleChangeShippingAddress = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setBillingAddress((values) => ({ ...values, [name]: value }))
  }

  const onCompletedNewOrg = (data: any) => {
    if (data?.createOrganization?.errors?.length > 0) {
      console.log("user needs to fill in fields correctly")
    } else {
      window.location.reload()
    }
  }
  const onErrorNewOrg = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error creating new org")
    setError(true)
    setTimeout(() => setError(false), 2000)
  }
  const { createNewOrganization, data } = useCreateNewOrganization({
    onCompleted: (data: any) => onCompletedNewOrg(data),
    onError: onErrorNewOrg,
  })
  const onCreateNewOrg = async (event: any) => {
    event.preventDefault()

    try {
      await createNewOrganization({
        variables: {
          input: {
            name: newOrgName,
            address1: billingAddress?.address1,
            address2: billingAddress?.address2,
            country: billingAddress?.country ?? "AU",
            city: billingAddress?.city,
            zipCode: billingAddress?.zipCode,
            isSubOrg: false,
            vat: billingAddress?.vat,
          },
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error creating new org")
    }
  }

  const onCompletedAcceptedJoinRequest = () => {
    console.log("accepted organization join request")
    window.location.reload()
  }
  const onErrorAcceptedJoinRequest = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error accepting org join request")
  }
  const { acceptOrgJoinRequest } = useAcceptOrgJoinRequest({
    onCompleted: onCompletedAcceptedJoinRequest,
    onError: onErrorAcceptedJoinRequest,
  })
  const onAcceptOrganizationJoinRequest = async (id: number | string) => {
    try {
      await acceptOrgJoinRequest({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error accepting org join request")
    }
  }

  const onCompletedRemoveJoinReq = () => {
    console.log("Removed org join request")
  }
  const onErrorRemoveJoinReq = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing org join request")
  }
  const { removeOrgJoinRequest } = useRemoveOrgJoinRequest({
    onCompleted: onCompletedRemoveJoinReq,
    onError: onErrorRemoveJoinReq,
  })
  const onRemoveJoinOrgRequest = async (id: number | string) => {
    try {
      await removeOrgJoinRequest({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing org join request")
    }
  }

  return (
    <div>
      {userJoinOrgRequest?.joinRequests?.map(
        (req: {
          fromUser: { email: string; id: number | string }
          id: number | string
          organization: { id: number | string; name: string }
          toUser: string
        }) => (
          <Row justify="center" key={req.id}>
            <Col md={8}>
              <Card>
                <CardContent>
                  <Subtitle textAlign="center">
                    You have been invited to join {req.organization.name} by {req.fromUser.email}
                  </Subtitle>
                  <HorizontalSpacer spacing={2} />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <SecondaryButton title="Accept" onClick={() => onAcceptOrganizationJoinRequest(req.id)} />
                    <Spacer spacing={1} />
                    <PrimaryButton title="Decline" onClick={() => onRemoveJoinOrgRequest(req.id)} />
                  </div>
                </CardContent>
              </Card>
            </Col>
          </Row>
        ),
      )}

      <HorizontalSpacer spacing={4} />

      <Row>
        <Col md={6}>
          <Subtitle>You are not part of an organization</Subtitle>
          <HorizontalSpacer spacing={1} />
          <Body>
            If your company already has an Organization, ask your administrator to invite you. If you are the
            administrator, you can create a new Organization below.
          </Body>
          <HorizontalSpacer spacing={1} />
          <Body>With Organization, you can:</Body>
          <HorizontalSpacer spacing={1} />
          <Body>・Manage and organize terminals</Body>
          <Body>・View terminal statistics and data consumptions of your terminals and plans</Body>
          <Body>・Complete purchases in the store</Body>
          <Body>・Manage your organization users by creating groups and suborganizations</Body>
          <Body>・Manage who has access to what terminals with groups </Body>
          <HorizontalSpacer spacing={1} />
        </Col>
        <Col md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
          <Image src="https://images.ctfassets.net/kzqg847wz5jr/3TOWydHOoeRsPojBQw0lBN/01fc3f0f8b047682dfe5aa0425751760/wwd-fire__1_.webp" />
        </Col>
      </Row>
      <HorizontalSpacer spacing={4} />
      <Card noPadding>
        <CardBar open={addOrgOpen}>
          <Row justify="center">
            <HoverLinkText
              title="Create new organization"
              color={theme.colors.white}
              icon={<AddIcon color={theme.colors.white} />}
              onClick={() => setAddOrgOpen(!addOrgOpen)}
            />
          </Row>
        </CardBar>
        <SlideOutHeight isVisible={addOrgOpen}>
          <OrganizationContent>
            <Row justify="center">
              <Col md={4}>
                <TextInput placeholder="Organization name" onChange={(e) => setNewOrgName(e.target.value)} />
              </Col>
            </Row>
            <HorizontalSpacer spacing={3} />
            <H4>Billing address </H4>
            <HorizontalSpacer spacing={1} />
            <Row>
              <Col md={6}>
                <AddressForm onChange={handleChangeShippingAddress} />
              </Col>
            </Row>
            <HorizontalSpacer spacing={3} />

            {error && (
              <div>
                <HorizontalSpacer spacing={2} />
                <Body>You need to fill in the required fields</Body>
              </div>
            )}

            <form onSubmit={onCreateNewOrg}>
              {data?.createOrganization?.errors?.length > 0 && (
                <div>
                  <HorizontalSpacer spacing={2} />
                  {data.createOrganization.errors?.map((error: { messages: string; location: any }) => (
                    <Body key={error.messages} color={theme.colors.red}>
                      {error.messages}
                    </Body>
                  ))}
                  <HorizontalSpacer spacing={2} />
                </div>
              )}

              <Row justify="center">
                <SecondaryButton title="Create organization" type="submit" disabled={!billingAddress} />

                <Spacer spacing={1} />
                <PrimaryButton title="Cancel" onClick={() => setAddOrgOpen(false)} />
              </Row>
            </form>
          </OrganizationContent>
        </SlideOutHeight>
      </Card>
    </div>
  )
}

export default LandingPage
